import React, {useCallback} from 'react';
import styled from 'styled-components';
import {tint} from 'theme/helper';
import {DrawerPreset} from 'components/customer/Product/Drawer/helpers/drawerPresets';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';

interface PresetRowProps {
    presets: (DrawerPreset & {isActive: boolean})[];
    onApply: (index: number) => void;
}

// The shapes a layout can start from, drawn to scale. What the heights mean and
// how applying one is written back belongs to whoever renders this.
export const PresetRow = ({presets, onApply}: PresetRowProps) => {
    const compact = useCompactLayout();
    const applyHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            onApply(Number(event.currentTarget.dataset.preset));
        },
        [onApply]
    );

    if (presets.length === 0) return null;

    return (
        <Column data-cy="drawer-presets" $compact={compact}>
            <Heading>Start from</Heading>
            {presets.map((preset, index) => {
                const total = preset.shape.reduce((sum, size) => sum + size, 0);

                return (
                    <Option
                        key={preset.label}
                        type="button"
                        data-preset={index}
                        data-cy={`drawer-preset-${preset.label
                            .toLowerCase()
                            .replace(/\s+/g, '-')}`}
                        data-active={preset.isActive ? 'true' : 'false'}
                        aria-disabled={preset.isCustom === true}
                        onClick={applyHandler}
                        $active={preset.isActive}
                        $compact={compact}
                        $custom={preset.isCustom === true}
                        title={
                            preset.isCustom
                                ? 'These heights match none of the presets'
                                : `Apply the ${preset.label} layout`
                        }>
                        <Thumb $compact={compact}>
                            {preset.shape.map((size, bar) => (
                                <Bar
                                    // eslint-disable-next-line react/no-array-index-key -- bars are positional and never reorder
                                    key={bar}
                                    $grow={total > 0 ? size / total : 1}
                                    $active={preset.isActive}
                                />
                            ))}
                        </Thumb>
                        <Label>{preset.label}</Label>
                    </Option>
                );
            })}
        </Column>
    );
};

// a band across the top, so the diagram below keeps the card's width; folded,
// a side column left it too narrow, or an empty gap once it wrapped under
const Column = styled.div<{$compact: boolean}>`
    flex: 1 1 100%;
    gap: ${({$compact}) => ($compact ? '6px' : '8px')};
    flex-wrap: wrap;
    ${({$compact}) =>
        $compact
            ? `display: grid;
               grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));`
            : `display: flex;
               flex-direction: row;
               align-items: center;`}
`;

const Heading = styled.div`
    grid-column: 1 / -1;
    flex: 0 0 auto;
    margin-right: 4px;
    color: #6b7480;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
`;

const Option = styled.button<{
    $active: boolean;
    $compact: boolean;
    $custom: boolean;
}>`
    /* wide enough to read, and they share the line evenly; one on its own
       stays a thumbnail rather than stretching the width of the card */
    ${({$compact}) =>
        $compact
            ? `flex: 0 0 auto; width: 100%; max-width: none;`
            : /* no basis to overflow, so Custom never pushes a line break */
              `flex: 1 1 0; max-width: 200px;`}
    min-width: 0;
    display: flex;
    align-items: center;
    gap: ${({$compact}) => ($compact ? '8px' : '9px')};
    padding: ${({$compact}) => ($compact ? '5px 8px' : '6px 9px')};
    border-radius: 5px;
    font: inherit;
    text-align: left;
    background: ${({$active, theme}) =>
        $active ? tint(theme.colors.primary?.main, 0.1) : '#fff'};
    border: ${({$active, $custom, theme}) =>
        $active
            ? `2px ${$custom ? 'dashed' : 'solid'} ${
                  theme.colors.primary?.main
              }`
            : `1px ${$custom ? 'dashed' : 'solid'} ${tint(
                  theme.colors.primary?.main,
                  0.18
              )}`};

    /* bootstrap's reboot sets a pointer on every enabled button, at a higher
       specificity than a single class, so the Custom marker needs the extra && */
    && {
        cursor: ${({$custom}) => ($custom ? 'default' : 'pointer')};
    }
`;

const Thumb = styled.span<{$compact: boolean}>`
    width: ${({$compact}) => ($compact ? 20 : 24)}px;
    height: ${({$compact}) => ($compact ? 32 : 40)}px;
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px;
    border-radius: 2px;
    box-sizing: border-box;
    border: 1px solid ${({theme}) => tint(theme.colors.primary?.main, 0.25)};
    background: ${({theme}) => tint(theme.colors.primary?.main, 0.05)};
`;

const Bar = styled.span<{$grow: number; $active: boolean}>`
    flex: ${({$grow}) => $grow} 1 0;
    border-radius: 1px;
    background: ${({$active, theme}) =>
        tint(theme.colors.primary?.main, $active ? 0.45 : 0.22)};
`;

const Label = styled.span`
    flex: 1;
    min-width: 0;
    font-weight: 700;
    font-size: 12px;
    line-height: 1.25;
    text-wrap: pretty;
    color: ${({theme}) => theme.colors.primary?.main};
`;
